import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Sends no `X-Camptain-Active-Role`: the call acts with every role the person holds, not the
     * chosen one (e.g. GET /api/me, which lists the roles, and the Fabrica admin API, whose
     * platform role the chosen school role would drop). See {@link withoutActiveRole}.
     */
    withoutActiveRole?: boolean;
  }
}

/** Field name → messages, from an ASP.NET `ValidationProblemDetails` response. */
export type FieldErrors = Record<string, string[]>;

/**
 * Normalised error shape surfaced to the UI. Every rejected request from
 * {@link apiClient} rejects with an {@link ApiError}, so components and hooks
 * never have to reach into axios internals.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly details?: unknown;
  /** Per-field validation messages (400 responses), keyed by field name. */
  readonly fieldErrors?: FieldErrors;

  constructor(message: string, status: number, details?: unknown, fieldErrors?: FieldErrors) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.fieldErrors = fieldErrors;
  }
}

/** Error body shapes the API returns: ASP.NET ProblemDetails, or a legacy `{ message }`. */
interface ErrorBody {
  message?: string;
  title?: string;
  detail?: string;
  errors?: FieldErrors;
}

/** The message a user should see for a failed request. */
export function errorMessageFromBody(body: unknown, status: number, fallback: string): string {
  const data = (body && typeof body === 'object' ? body : {}) as ErrorBody;
  const firstFieldError = data.errors
    ? Object.values(data.errors).flat().find((m) => typeof m === 'string' && m.length > 0)
    : undefined;
  return (
    data.message ??
    firstFieldError ??
    data.detail ??
    data.title ??
    (status === 0 ? 'Unable to reach the server. Check your connection and try again.' : fallback)
  );
}

export function resolveBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL?.trim();
  // The app always calls "/api/..." relative paths. In dev, Vite proxies them.
  // If VITE_API_URL points at a bare origin we still call relative — the proxy
  // (dev) or same-origin deploy (prod) handles routing. When the API lives on
  // another origin in prod, set VITE_API_URL to include it and we prefix here.
  if (!configured || configured.startsWith('/')) return configured || '';
  try {
    const url = new URL(configured);
    // Keep only the origin; per-request paths already start with /api.
    return url.origin;
  } catch {
    return '';
  }
}

// ── Request context ────────────────────────────────────────────────────────
// The API scopes every /api call to an organization (and optionally a branch)
// and records the acting user. Until sign-in supplies them, they come from
// VITE_ORGANIZATION_ID / VITE_BRANCH_ID / VITE_USER_ID.

export const REQUEST_CONTEXT_HEADERS = {
  organizationId: 'X-Organization-Id',
  branchId: 'X-Branch-Id',
  userId: 'X-User-Id',
  /** The working academic year; without it the API uses the branch's current year. */
  academicYearId: 'X-Academic-Year-Id',
  /** The role the person is acting as (DebtCollectify's active-role header); the API narrows the call to it. */
  activeRoleId: 'X-Camptain-Active-Role',
} as const;

export interface RequestContext {
  organizationId?: number | null;
  branchId?: number | null;
  userId?: number | null;
  academicYearId?: number | null;
  activeRoleId?: number | null;
}

const envId = (value: string | undefined): number | null => {
  const id = Number(value?.trim());
  return Number.isInteger(id) && id > 0 ? id : null;
};

/** The request context configured through environment variables. */
export const envRequestContext = (): RequestContext => ({
  organizationId: envId(import.meta.env.VITE_ORGANIZATION_ID),
  branchId: envId(import.meta.env.VITE_BRANCH_ID),
  userId: envId(import.meta.env.VITE_USER_ID),
});

type TokenProvider = () => string | null | undefined | Promise<string | null | undefined>;
let authTokenProvider: TokenProvider | undefined;
let requestContextProvider: () => RequestContext = envRequestContext;
let branchResolver: ((url: string) => Promise<number | null> | number | null) | undefined;
let staleAcademicYearHandler: ((academicYearId: number) => void) | undefined;

/** Register a callback that supplies a bearer token for outgoing requests. */
export function setAuthTokenProvider(provider: TokenProvider): void {
  authTokenProvider = provider;
}

/** The bearer token outgoing requests carry, for clients outside apiClient (the ported Fabrica pages). */
export async function getAuthToken(): Promise<string | null> {
  return (await authTokenProvider?.()) || null;
}

/** Register a callback that supplies the organization / branch / user for outgoing requests. */
export function setRequestContextProvider(provider: () => RequestContext): void {
  requestContextProvider = provider;
}

/**
 * Register how a request that names no branch finds one (by its URL), e.g. the
 * routes that act for the school chosen in the header. It runs after the request
 * context, so a branch from the context or `branchScope` is never replaced.
 */
export function setBranchResolver(resolver: (url: string) => Promise<number | null> | number | null): void {
  branchResolver = resolver;
}

/**
 * Register what happens when the API rejects the working academic year of the request
 * context (a 400 naming `academicYearId` for a request that sent it as
 * `X-Academic-Year-Id`), e.g. a year since deleted or from another school. The handler
 * gets the rejected year id; `undefined` removes it.
 */
export function setStaleAcademicYearHandler(handler: ((academicYearId: number) => void) | undefined): void {
  staleAcademicYearHandler = handler;
}

/** The validation field the API names when it rejects the `X-Academic-Year-Id` header. */
const ACADEMIC_YEAR_FIELD = 'academicyearid';

/**
 * The working year the API rejected for this failed request, or null. Only a year the
 * request sent in its header that is still the context's working year counts: a year a
 * caller named in its body, query or own header is the caller's to handle.
 */
function rejectedWorkingYear(error: AxiosError): number | null {
  if (error.response?.status !== 400) return null;
  const errors = (error.response.data as ErrorBody | undefined)?.errors;
  if (!errors || typeof errors !== 'object') return null;
  if (!Object.keys(errors).some((field) => field.toLowerCase() === ACADEMIC_YEAR_FIELD)) return null;
  const sent = Number(error.config?.headers?.get(REQUEST_CONTEXT_HEADERS.academicYearId));
  if (!Number.isInteger(sent) || sent <= 0) return null;
  return requestContextProvider().academicYearId === sent ? sent : null;
}

/** `config` for a call that acts with every role the person holds: no `X-Camptain-Active-Role`. */
export const withoutActiveRole = (config?: AxiosRequestConfig): AxiosRequestConfig => ({ ...config, withoutActiveRole: true });

/**
 * Request config that names the branch a call acts for (`X-Branch-Id`), for the
 * API's branch-scoped routes. It wins over the branch of the request context.
 * `null` leaves the request organization-wide.
 */
export const branchScope = (branchId: number | null): AxiosRequestConfig | undefined =>
  branchId == null ? undefined : { headers: { [REQUEST_CONTEXT_HEADERS.branchId]: String(branchId) } };

/**
 * Whether a request acts for the current school's branch, so it may carry the working
 * year: true when the caller named no branch, or named the branch of the school chosen in
 * the header (the one {@link setBranchResolver} finds for its URL). The working year is
 * that school's, so the request context's branch (e.g. `VITE_BRANCH_ID`) does not count.
 * A branch that differs, that cannot be compared, or whose lookup fails, belongs to
 * another school: no year header.
 */
async function actsForCurrentSchoolBranch(config: InternalAxiosRequestConfig): Promise<boolean> {
  if (!config.headers.has(REQUEST_CONTEXT_HEADERS.branchId)) return true;
  const callerBranch = Number(config.headers.get(REQUEST_CONTEXT_HEADERS.branchId));
  try {
    const currentBranch = branchResolver ? await branchResolver(config.url ?? '') : null;
    return currentBranch != null && currentBranch === callerBranch;
  } catch {
    return false;
  }
}

function createClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: resolveBaseUrl(),
    timeout: 30_000,
    headers: { Accept: 'application/json' },
  });

  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await authTokenProvider?.();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    const context = requestContextProvider();
    // A call the caller scoped to a branch itself (see branchScope / schoolScope) may act
    // for another school, whose years differ from the working year: it gets no year from
    // the context. One scoped to the current school's branch keeps the working year.
    const sendsContextYear = await actsForCurrentSchoolBranch(config);
    for (const key of Object.keys(REQUEST_CONTEXT_HEADERS) as (keyof RequestContext)[]) {
      if (key === 'academicYearId' && !sendsContextYear) continue;
      if (key === 'activeRoleId' && config.withoutActiveRole) continue;
      const value = context[key];
      // A header the caller set itself (see branchScope) is kept.
      if (value && !config.headers.has(REQUEST_CONTEXT_HEADERS[key])) {
        config.headers.set(REQUEST_CONTEXT_HEADERS[key], String(value));
      }
    }

    if (branchResolver && !config.headers.has(REQUEST_CONTEXT_HEADERS.branchId)) {
      const branchId = await branchResolver(config.url ?? '');
      if (branchId) config.headers.set(REQUEST_CONTEXT_HEADERS.branchId, String(branchId));
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (error instanceof AxiosError) {
        const status = error.response?.status ?? 0;
        const body: unknown = error.response?.data;
        const fieldErrors = (body as ErrorBody | undefined)?.errors;
        const staleYear = rejectedWorkingYear(error);
        if (staleYear !== null) staleAcademicYearHandler?.(staleYear);
        return Promise.reject(
          new ApiError(errorMessageFromBody(body, status, error.message), status, body, fieldErrors),
        );
      }
      return Promise.reject(
        error instanceof Error ? new ApiError(error.message, 0) : new ApiError('Unknown error', 0),
      );
    },
  );

  return instance;
}

export const apiClient = createClient();

/** GET returning the response body directly. */
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await apiClient.get<T>(url, config);
  return data;
}

/** POST returning the response body directly. */
export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await apiClient.post<T>(url, body, config);
  return data;
}

/**
 * A read's inputs as the body of its POST (a read with more than two inputs): what a
 * query string would have left empty (undefined, null, '') is left out, so the API's
 * numbers and dates bind as "not given" instead of failing.
 */
export function queryBody(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));
}

/** DELETE returning the response body directly. */
export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await apiClient.delete<T>(url, config);
  return data;
}

// ── Paged lists ────────────────────────────────────────────────────────────

/** One page of a list, as returned by the Camptain API. */
export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** The API's largest page size. */
export const MAX_PAGE_SIZE = 100;

/**
 * One page of a paged list. Lists are read by POST (page, pageSize, search, sorting and
 * filters are more inputs than a GET takes) at `{collection}/search`: a POST on the
 * collection itself creates. `params` (and any `config.params`) go in the body.
 */
export function searchPage<T>(
  collection: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<PagedResult<T>> {
  const extra = config?.params as Record<string, unknown> | undefined;
  return postPage<T>(`${collection}/search`, { ...params, ...extra }, { ...config, params: undefined });
}

/**
 * Every row of a paged list, by walking its pages. For dropdowns and other screens
 * that need the full list; `params` are the list's own filters.
 */
export function searchAllPages<T>(
  collection: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<T[]> {
  return allPages((page) => searchPage<T>(collection, { ...params, ...page }, config));
}

/** {@link searchPage} for a list read at its own named route (e.g. `GetApplicantsPage`), not `/search`. */
export function postPage<T>(
  url: string,
  body: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<PagedResult<T>> {
  return post<PagedResult<T>>(url, queryBody(body), config);
}

/** {@link searchAllPages} for a list read at its own named route. */
export function postAllPages<T>(
  url: string,
  body: Record<string, unknown> = {},
  config?: AxiosRequestConfig,
): Promise<T[]> {
  return allPages((page) => postPage<T>(url, { ...body, ...page }, config));
}

async function allPages<T>(
  read: (page: { page: number; pageSize: number }) => Promise<PagedResult<T>>,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page++) {
    const result = await read({ page, pageSize: MAX_PAGE_SIZE });
    items.push(...result.items);
    if (result.items.length === 0 || items.length >= result.totalCount) return items;
  }
}
